import {
  Button as AButton,
  DatePicker,
  Divider,
  Form,
  Radio,
  Select,
  Skeleton,
  Space,
  Tooltip,
  Checkbox,
  Input,
} from "antd";
const { RangePicker } = DatePicker;
const { Option } = Select;
import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import screenfull from "screenfull";
import { jt, t } from "ttag";
import _ from "underscore";
import { siteLocale } from "metabase/lib/i18n";

import {
  useListCardsQuery,
  useListPublicCardsQuery,
  useListPublicDashboardsQuery,
} from "metabase/api";
import { useDocsUrl, useSetting } from "metabase/common/hooks";
import ExternalLink from "metabase/core/components/ExternalLink";
import Link from "metabase/core/components/Link";
import CS from "metabase/css/core/index.css";
import { useSelector } from "metabase/lib/redux";
import { getUserIsAdmin } from "metabase/selectors/user";
import { Box, Button, Group, Icon, Stack, Text } from "metabase/ui";
import type {
  Dashboard,
  VirtualDashboardCard,
  VisualizationSettings,
} from "metabase-types/api";

import { isUrlString } from "../LinkViz/utils";

import {
  CustomBtnsWrapper,
  IFrameEditWrapper,
  IFrameWrapper,
  InteractiveText,
  StyledInput,
} from "./IFrameViz.styled";
import { settings } from "./IFrameVizSettings";
import { isAllowedIframeUrl } from "./utils";

export interface IFrameVizProps {
  dashcard: VirtualDashboardCard;
  dashboard: Dashboard;
  isEditing: boolean;
  isPreviewing: boolean;
  onUpdateVisualizationSettings: (newSettings: VisualizationSettings) => void;
  settings: VisualizationSettings;
  isEditingParameter?: boolean;
  width: number;
  height: number;
  gridSize: {
    width: number;
    height: number;
  };
  totalNumGridCols: number;
  onTogglePreviewing: () => void;
}

export function IFrameViz({
  dashboard,
  dashcard,
  isEditing,
  onUpdateVisualizationSettings,
  settings,
  isEditingParameter,
  width,
  height,
  gridSize,
  totalNumGridCols,
  isPreviewing,
  onTogglePreviewing,
}: IFrameVizProps) {
  const { iframe: iframeOrUrl } = settings;
  const isNew = !!dashcard?.justAdded;

  const [showCustomFilter, setShowCustomFilter] = useState(false);
  const [customFilters, setCustomFilters] = useState<{ [key: string]: string }>(
    {},
  );
  // 保存用户正在输入的值
  const [customFilterInput, setCustomFilterInput] = useState<{
    [key: string]: string;
  }>({});

  const [dateRangeValue, setDateRangeValue] = useState("thisday");
  const [radioValue, setRadioValue] = useState("month");
  const [loading, setLoading] = useState<boolean>(true);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());
  // 添加一个状态来跟踪当前选择的容器类型
  const [selectedContainerType, setSelectedContainerType] = useState<
    string | undefined
  >();
  //支付方式
  const [paymentMethods, setPaymentMethods] = useState<
    { label: string; value: string }[]
  >([]);
  //控制筛选框显隐
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const customTypeList = ["TwoRowCard", "TwoRowCardFlex"];
  const language = window.MetabaseUserLocalization.headers.language;
  console.log("language", language);

  const token = (window as any).Metabase.store.getState().app.tempStorage.token;

  const WEBPACK_BUNDLE = process.env.WEBPACK_BUNDLE || "development";
  const devMode = WEBPACK_BUNDLE !== "production";

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeWrapperRef = useRef<HTMLDivElement>(null);
  const siteUrl = useSetting("site-url");
  const allowedHosts = useSetting("allowed-iframe-hosts");
  const queryDashboards = useListPublicDashboardsQuery();
  const queryCards = useListPublicCardsQuery();
  const queryCardsList = useListCardsQuery();
  const publicOptions = useMemo(() => {
    const arr: any = [];
    if (queryDashboards.status === "fulfilled") {
      queryDashboards.data?.forEach(l => {
        if (l.id !== dashboard.id) {
          arr.push({
            type: "dashboard",
            label: l.name,
            value: l.public_uuid,
          });
        }
      });
    }
    if (queryCards.status === "fulfilled") {
      queryCards.data?.forEach(l => {
        arr.push({
          type: "question",
          label: l.name,
          value: l.public_uuid,
        });
      });
    }
    return arr;
  }, [queryDashboards, queryCards, dashboard.id]);
  const customOptions = useMemo(() => {
    const arr: any = [];
    if (queryCardsList.status === "fulfilled") {
      queryCardsList.data?.forEach(l => {
        arr.push({
          label: l.name,
          value: l.id,
          id: l.id,
        });
      });
    }
    return arr;
  }, [queryCardsList]);

  useEffect(() => {
    const shouldFetch =
      dashcard.visualization_settings.showFilterButton &&
      dashcard.visualization_settings.filterComponentType === "filter2";

    if (shouldFetch) {
      const fetchPaymentMethods = async () => {
        const params = { tableType: "PAYMENT_PAY_TYPE" };
        const queryString = new URLSearchParams(params).toString();
        const url = `/cisapi/system/api/sys/dictionary/public-dict-combo-ex?${queryString}`;
        const token = (window as any).Metabase.store.getState().app.tempStorage
          .token;

        try {
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Access-Token": token ?? "默认token", // 可替换为开发token
            },
          });

          const json = await response.json();
          if (json.result && typeof json.result === "object") {
            const entries = Object.entries(json.result);
            setPaymentMethods(
              entries.map(([key, item]: [string, any]) => ({
                // label: item.text,
                label:
                  language === "zh-CN" || language === "zh"
                    ? item.text
                    : language === "fr"
                      ? item.v2
                      : item.v1,
                value: key,
              })),
            );
          }
        } catch (error) {
          console.error("获取支付方式失败", error);
        }
      };

      fetchPaymentMethods();
    }
  }, []);

  // 监听点击事件，控制筛选弹窗
  useEffect(() => {
    if (!showCustomFilter) return;
    const handleMouseDown = (event: MouseEvent) => {
      const panel = filterPanelRef.current;
      const target = event.target as HTMLElement;
      const isInSelectDropdown = !!target.closest(".ant-select-dropdown");
      const isInSelectDropdown1 = !!target.closest(".ant-select-clear");

      if (
        panel &&
        !panel.contains(target) &&
        !isInSelectDropdown &&
        !isInSelectDropdown1
      ) {
        setShowCustomFilter(false);
      }
    };
    const handleBlur = () => {
      setShowCustomFilter(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("blur", handleBlur);
    };
  }, [showCustomFilter]);

  const regexpArr = /src="(\S*)"/.exec(iframeOrUrl || "");
  let iframeUrl = regexpArr?.length ? regexpArr[1] : "";

  const handleIFrameChange = useCallback(
    (newIFrame: string) => {
      iframeUrl = newIFrame;
      onUpdateVisualizationSettings({
        iframe: `<iframe src="${newIFrame}" />`,
      });
    },
    [onUpdateVisualizationSettings],
  );
  const handleFormChange = useCallback(
    (allValues: any) => {
      let publicUrl = "";
      let publicType = "dashboard";
      // 处理自定义URL的情况 - 直接使用用户输入的URL
      if (
        customTypeList.includes(allValues.containerType) &&
        allValues.customDataCardId
      ) {
        publicUrl = `${siteUrl}/metabasecard/index.html?customIframe=true&type=${allValues.containerType}&id=${allValues.customDataCardId}&lang=${language}&token=${token}&width=${width}&height=${height}&gridSize=${JSON.stringify(gridSize)}`;
        // 直接更新iframe设置，不进行任何处理
        onUpdateVisualizationSettings({
          iframe: `<iframe src="${publicUrl}" />`,
          ...allValues,
          hiddenToolbar: true,
          hiddenFilter: true,
          showRefreshButton: false,
          showFullButton: false,
          publicType,
        });
        return; // 直接返回，不执行后续处理
      }
      // 原有的公共仪表板和卡片逻辑
      else if (allValues.containerUuid) {
        const publicItem = publicOptions.find(
          (l: any) => l.value === allValues.containerUuid,
        );
        if (publicItem) {
          publicUrl = `${siteUrl}/public/${publicItem.type}/${publicItem.value}`;
          publicType = publicItem.type;
        }

        handleIFrameChange(publicUrl);
        Object.assign(allValues, {
          hiddenToolbar: true,
          hiddenFilter: true,
          publicType,
        });
      }
      onUpdateVisualizationSettings(allValues);
    },
    [onUpdateVisualizationSettings, publicOptions],
  );
  const [form] = Form.useForm();
  const onClearClick = () => {
    const initObj = {
      containerType: undefined,
      containerUuid: undefined,
      customDataCardId: undefined,
    };
    form.setFieldsValue(initObj);
    handleFormChange(initObj);
  };
  if (isEditing && !isEditingParameter && !isPreviewing) {
    return (
      <IFrameEditWrapper>
        <Stack h="100%" spacing="sm">
          <Group align="center" noWrap>
            <Text fw="bold" truncate>
              {t`Container`}
            </Text>{" "}
            <Box ml="auto">
              <Button
                compact
                variant="default"
                style={{ pointerEvents: "all", marginRight: "4px" }}
                onClick={onClearClick}
                onMouseDown={e => e.stopPropagation()}
              >{t`Clear`}</Button>
              <Button
                compact
                variant="filled"
                style={{ pointerEvents: "all" }}
                onClick={onTogglePreviewing}
                onMouseDown={e => e.stopPropagation()}
              >{t`Done`}</Button>
            </Box>
          </Group>
          <Box h="100%" style={{ maxHeight: height - 48, overflow: "auto" }}>
            <StyledInput
              data-testid="iframe-card-input"
              autoFocus={isNew}
              size="100%"
              styles={{
                wrapper: {
                  height: "100%",
                },
              }}
              h="50%"
              value={iframeUrl}
              placeholder={`https://example.com`}
              onChange={e => handleIFrameChange(e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              style={{ pointerEvents: "all", display: "none" }}
            />
            <Form
              style={{ marginTop: "8px", maxWidth: 600 }}
              form={form}
              layout="vertical"
              initialValues={dashcard.visualization_settings}
              onValuesChange={(changedValues, allValues) => {
                if (changedValues.containerType) {
                  setSelectedContainerType(changedValues.containerType);
                }
                handleFormChange(allValues);
              }}
            >
              <Form.Item
                name="containerType"
                label={t`Please select container type`}
              >
                <Select placeholder={t`Please select`} allowClear>
                  <Option value="customDateRange">{t`Time Container`}</Option>
                  <Option value="customTimeGroup">{t`Tab - Day/Month/Year Container`}</Option>
                  <Option value="customTimeGroup1">{t`Tab - Month/Quarter/Year Container`}</Option>
                  <Option value="customFilterPanel">{t`Custom Filter Panel`}</Option>
                  {/* 新增自定义URL选项 */}
                  <Option value="TwoRowCard">{t`Two Row Card`}</Option>
                  <Option value="TwoRowCardFlex">{t`Two Row Card Flex`}</Option>
                </Select>
              </Form.Item>
              {/* 当选择自定义URL时显示URL输入框 */}
              {customTypeList.includes(selectedContainerType) && (
                <Form.Item
                  name="customDataCardId"
                  label={t`Select custom data`}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={t`Please select`}
                    options={customOptions}
                    allowClear
                  />
                </Form.Item>
              )}

              {/* 原有的仪表板和卡片选择器，只在未选择自定义URL时显示 */}
              {!customTypeList.includes(selectedContainerType) && (
                <Form.Item
                  name="containerUuid"
                  label={t`Select the data dashboard or card from below`}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={t`Please select`}
                    options={publicOptions}
                    allowClear
                  ></Select>
                </Form.Item>
              )}
              {form.getFieldValue("containerType") === "customFilterPanel" && (
                <Form.Item label={t`Custom Options`}>
                  <Space direction="vertical">
                    {/* 是否显示刷新按钮 */}
                    <Form.Item
                      name="showRefreshButton"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>{t`Show Refresh Button`}</Checkbox>
                    </Form.Item>
                    {/* 是否显示全屏按钮 */}
                    <Form.Item
                      name="showFullButton"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>{t`Show Full Screen Button`}</Checkbox>
                    </Form.Item>
                    {/* 是否显示筛选框按钮 */}
                    <Form.Item
                      name="showFilterButton"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>{t`Show Filter Button`}</Checkbox>
                    </Form.Item>
                    {/* 是否显示自身筛选器 */}
                    <Form.Item
                      name="showSelfFilter"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>{t`Show Self Filter`}</Checkbox>
                    </Form.Item>
                    {/* 日期选择器是否设置默认值 */}
                    <Form.Item
                      name="isDatePickerDefault"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>{t`Whether the date picker sets default values`}</Checkbox>
                    </Form.Item>
                    {/* 日期选择器是否设置默认值 */}
                    <Form.Item
                      name="isMonthDefault"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>{t`Whether the month picker sets default values`}</Checkbox>
                    </Form.Item>
                    {/* 导出模板输入框 */}
                    <Form.Item
                      name="exportTemplate"
                      label={t`Export Template`}
                    >
                      <Input placeholder={t`Please enter template name`} />
                    </Form.Item>
                  </Space>
                </Form.Item>
              )}
              {form.getFieldValue("containerType") === "customFilterPanel" &&
                dashcard.visualization_settings.showFilterButton && (
                  <Form.Item label={t`Filter Type`} shouldUpdate>
                    {() => {
                      const currentType = form.getFieldValue(
                        "filterComponentType",
                      );
                      const updateFilterType = (type: string) => {
                        form.setFieldsValue({ filterComponentType: type });
                        handleFormChange({
                          ...form.getFieldsValue(),
                          filterComponentType: type,
                        });
                      };

                      return (
                        <Space>
                          <Checkbox
                            checked={currentType === "filter1"}
                            onChange={() => updateFilterType("filter1")}
                          >
                            Filter1
                          </Checkbox>
                          <Checkbox
                            checked={currentType === "filter2"}
                            onChange={() => updateFilterType("filter2")}
                          >
                            Filter2
                          </Checkbox>
                        </Space>
                      );
                    }}
                  </Form.Item>
                )}
            </Form>
          </Box>
        </Stack>
      </IFrameEditWrapper>
    );
  }
  const src = iframeUrl;
  const hasAllowedIFrameUrl = true;
  // const hasAllowedIFrameUrl =
  //   iframeUrl && isAllowedIframeUrl(iframeUrl, allowedHosts);
  const hasForbiddenIFrameUrl =
    iframeUrl && !isAllowedIframeUrl(iframeUrl, allowedHosts);
  const hiddenToolbar = src && dashcard.visualization_settings.hiddenToolbar;
  const hiddenFilter = src && dashcard.visualization_settings.hiddenFilter;
  //判断
  const hasCustomFilterPanel =
    src &&
    dashcard.visualization_settings.containerType === "customFilterPanel";
  const hasCustomDateRange =
    src && dashcard.visualization_settings.containerType === "customDateRange";
  const hasCustomTimeGroup =
    src && dashcard.visualization_settings.containerType === "customTimeGroup";
  const hasCustomTimeGroup1 =
    src && dashcard.visualization_settings.containerType === "customTimeGroup1";
  const hasCustomIframeUrl =
    src &&
    customTypeList.includes(dashcard.visualization_settings.containerType);

  // 根据容器类型决定是否处理URL

  const updateLoadingAndTime = () => {
    setLoading(true);
    setLastRefreshTime(Date.now());
  };
  let url: URL | null = null;
  if (isUrlString(src)) {
    url = new URL(src || "");
    if (dashcard?.visualization_settings?.isRefresh) {
      const params = sessionStorage.getItem(
        `${dashcard?.visualization_settings?.containerUuid}`,
      );
      if (params) {
        const paramsObj = JSON.parse(params);
        Object.keys(paramsObj)?.forEach(key => {
          paramsObj[key] && url?.searchParams.set(key, paramsObj[key]);
        });
      }
    }

    //设置默认值
    //判断url参数上是否有date_picker，如果有则不设置默认值
    const hasDatePicker = url.searchParams.has("date_picker");
    if (!hasDatePicker && dashcard.visualization_settings.isDatePickerDefault) {
      // 设置默认日期范围为最近7天
      const startDate = dayjs().add(-6, "day").format("YYYY-MM-DD");
      const endDate = dayjs().format("YYYY-MM-DD");
      url.searchParams.set("date_picker", `${startDate}~${endDate}`);
    }
    if (!hasDatePicker && dashcard.visualization_settings.isMonthDefault) {
      // 设置默认日期范围为最近7天
      const monthDate = dayjs().format("YYYY-MM");
      url.searchParams.set("date_picker", `${monthDate}`);
    }

    if (hiddenToolbar) {
      url.searchParams.set("hiddenToolbar", "true");
    } else {
      url.searchParams.delete("hiddenToolbar");
    }
    if (
      !hasCustomFilterPanel ||
      (hasCustomFilterPanel && !dashcard.visualization_settings.showSelfFilter)
    ) {
      url.searchParams.set("hiddenFilter", "true");
    } else {
      url.searchParams.delete("hiddenFilter");
    }
    //传递参数，控制自身筛选器靠右侧距离showSelfFilter
    if (hasCustomFilterPanel) {
      let buttonNum = 0;
      if (dashcard.visualization_settings.showRefreshButton) {
        buttonNum++;
      }
      if (dashcard.visualization_settings.showFilterButton) {
        buttonNum++;
      }
      if (dashcard.visualization_settings.showFullButton) {
        buttonNum++;
      }
      if (buttonNum > 0) {
        url.searchParams.set("buttonNum", buttonNum.toString());
      } else {
        url.searchParams.delete("buttonNum");
      }
    }
    //传递参数，控制隐藏和筛选框中相同字段的自身筛选器
    if (
      dashcard.visualization_settings.showSelfFilter &&
      dashcard.visualization_settings.showFilterButton
    ) {
      if (dashcard.visualization_settings.filterComponentType === "filter1") {
        url.searchParams.set("filters", "argreference,argoperator");
      } else if (
        dashcard.visualization_settings.filterComponentType === "filter2"
      ) {
        url.searchParams.set(
          "filters",
          "argreference,argtransactionid,argpaymentmethod,argoperator",
        );
      }
    }
    const token = (window as any).Metabase.store.getState().app.tempStorage
      .token;
    url.searchParams.set("token", token || "");
    if (hasCustomDateRange) {
      if (dateRangeValue) {
        url.searchParams.set("date_picker", dateRangeValue);
      } else {
        url.searchParams.delete("date_picker");
      }
    }
    if (hasCustomTimeGroup || hasCustomTimeGroup1) {
      if (radioValue) {
        url.searchParams.set("time_grouping", radioValue);
      } else {
        url.searchParams.delete("time_grouping");
      }
    }
      // 如果设置了导出模板，则拼接到 URL 上
      const template = dashcard.visualization_settings.exportTemplate?.trim();
      if (template) {
        url.searchParams.set("exportTemplate", template);
      } else {
        url.searchParams.delete("exportTemplate");
      }
    const time = Number(url.searchParams.get("time"));
    if (time > lastRefreshTime) {
      updateLoadingAndTime();
    }
    if (hasCustomIframeUrl) {
      url.searchParams.set("lang", language);
      url.searchParams.set("token", token || "");
      url.searchParams.set("width", width);
      url.searchParams.set("height", height);
      url.searchParams.set("gridSize", JSON.stringify(gridSize));
      url.searchParams.set("totalNumGridCols", totalNumGridCols);
      // 在开发模式下，使用本地开发服务器的URL
      const devUrl = "http://localhost:5173";
      const site = new URL(devMode ? devUrl : siteUrl);
      url.protocol = site.protocol;
      url.host = site.host;
      url.port = site.port;
    } else {
      const site = new URL(siteUrl);
      url.protocol = site.protocol;
      url.host = site.host;
      url.port = site.port;
    }
  }
  const datePresets = [
    {
      label: t`Today`,
      value: () => [dayjs().startOf("day"), dayjs().endOf("day")] as any,
      flag: "thisday",
    },
    {
      label: t`Yesterday`,
      value: () =>
        [
          dayjs().add(-1, "day").startOf("day"),
          dayjs().add(-1, "day").endOf("day"),
        ] as any,
      flag: "past1days",
    },
    {
      label: t`This Week`,
      value: () => [dayjs().startOf("week"), dayjs().endOf("week")] as any,
      flag: "thisweek",
    },
    {
      label: t`Last Week`,
      value: () =>
        [
          dayjs().add(-1, "week").startOf("week"),
          dayjs().add(-1, "week").endOf("week"),
        ] as any,
      flag: "past1weeks",
    },
    {
      label: t`This Month`,
      value: () => [dayjs().startOf("month"), dayjs().endOf("month")] as any,
      flag: "thismonth",
    },
    {
      label: t`Last Month`,
      value: () =>
        [
          dayjs().add(-1, "month").startOf("month"),
          dayjs().add(-1, "month").endOf("month"),
        ] as any,
      flag: "past1months",
    },
    {
      label: t`This Year`,
      value: () => [dayjs().startOf("year"), dayjs().endOf("year")] as any,
      flag: "thisyear",
    },
    {
      label: t`Last Year`,
      value: () =>
        [
          dayjs().add(-1, "year").startOf("year"),
          dayjs().add(-1, "year").endOf("year"),
        ] as any,
      flag: "past1years",
    },
    {
      label: t`Since System Official Launch`,
      value: () => [dayjs(0), dayjs()] as any,
      flag: "all",
    },
  ];
  let isPreset = false;
  const onPresetClick = (preset: any) => {
    isPreset = true;
    setDateRangeValue(preset.flag);
    updateLoadingAndTime();
    onUpdateVisualizationSettings({});
  };
  const onDateChange = (dates: any) => {
    if (!isPreset) {
      if (dates) {
        const startDate = dates[0].startOf("day").format("YYYY-MM-DD");
        const endDate = dates[1].endOf("day").format("YYYY-MM-DD");
        setDateRangeValue([startDate, endDate].join("~"));
      } else {
        setDateRangeValue("");
      }
      updateLoadingAndTime();
      onUpdateVisualizationSettings({});
    } else {
      isPreset = false;
    }
    // if (isUrlString(iframeUrl)) {
    //   const iframeUrl1 = new URL(iframeUrl || "");
    //   if (dates) {
    //     const startDate = dates[0].startOf("day").format("YYYY-MM-DDTHH:mm:ss");
    //     const endDate = dates[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss");
    //     iframeUrl1.searchParams.set(
    //       "date_range",
    //       [startDate, endDate].join("~"),
    //     );
    //   } else {
    //     iframeUrl1.searchParams.delete("date_range");
    //   }
    //   handleIFrameChange(iframeUrl1.href);
    // }
  };
  const onRadioChange = (e: any) => {
    setRadioValue(e.target.value);
    updateLoadingAndTime();
    onUpdateVisualizationSettings({});
    // if (isUrlString(iframeUrl)) {
    //   const iframeUrl1 = new URL(iframeUrl || "");
    //   iframeUrl1.searchParams.set("time_grouping", e.target.value);
    //   handleIFrameChange(iframeUrl1.href);
    // }
  };
  const onRefreshClick = () => {
    updateLoadingAndTime();
    onUpdateVisualizationSettings({
      isRefresh: true,
    });
    iframeRef.current?.contentWindow?.location?.reload();
  };
  const onFullscreenClick = () => {
    if (iframeRef.current) {
      screenfull.toggle(iframeRef.current);
    }
  };
  const renderError = () => {
    if (hasForbiddenIFrameUrl && isEditing) {
      return <ForbiddenDomainError url={iframeUrl} />;
    }
    return <GenericError />;
  };
  const onIframeLoad = () => {
    setLoading(false);
  };
  const handleFilterChange = (filters: Record<string, string>) => {
    //对比参数和旧参数，如果相同则不更新

    //将filters中参数为空字符串的参数去掉再对比
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => !!v),
    );
    console.log("handleFilterChange", JSON.stringify(cleanedFilters),JSON.stringify(customFilters));

    const cleanedFiltersTrim = Object.fromEntries(
      Object.entries(filters)
        .filter(([_, v]) => !!v)
        .map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    );
    const customFiltersTrim = Object.fromEntries(
      Object.entries(customFilters)
        .filter(([_, v]) => !!v)
        .map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    );
    if (
      JSON.stringify(cleanedFiltersTrim) ===
      JSON.stringify(customFiltersTrim)
    ) {
      console.log("Filters are the same, not updating.");
      return;
    }

    const key = `${dashcard?.visualization_settings?.containerUuid}`;
    const oldParams = sessionStorage.getItem(key);
    const processedFilters = Object.fromEntries(
      Object.entries(filters || {})?.map(([key, value]) => [
        key,
        value?.trim() === "" ? null : value?.trim(),
      ]),
    );
    const mergedParams = {
      ...(oldParams ? JSON.parse(oldParams) : {}),
      ...processedFilters,
    };
    sessionStorage.setItem(key, JSON.stringify(mergedParams));

    setCustomFilters(cleanedFilters);

    if (isUrlString(src)) {
      const url = new URL(src);

      // 先清空所有旧参数（可选）
      url.search = "";

      // 设置所有新参数
      Object.entries(cleanedFilters).forEach(([k, v]) => {
        url.searchParams.set(k, v.trim());
      });

      updateLoadingAndTime();
      onUpdateVisualizationSettings({
        isRefresh: true,
      });
      handleIFrameChange(url.href);
    }
  };
  return (
    <IFrameWrapper
      ref={iframeWrapperRef}
      data-testid="iframe-card"
      fade={isEditingParameter}
      customCard={!!hasCustomIframeUrl}
    >
      <Skeleton
        active
        avatar
        paragraph={{ rows: 12 }}
        loading={loading}
      ></Skeleton>
      {hasAllowedIFrameUrl ? (
        <iframe
          ref={iframeRef}
          data-testid="iframe-visualization"
          width={width}
          height={height}
          frameBorder={0}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
          src={url?.href}
          onLoad={onIframeLoad}
        />
      ) : (
        renderError()
      )}
      <CustomBtnsWrapper
        publicType={dashcard.visualization_settings.publicType as any}
      >
        {hasCustomFilterPanel &&
        dashcard.visualization_settings.showFilterButton ? (
          <>
            <AButton onClick={() => setShowCustomFilter(prev => !prev)}>
              {/* {showCustomFilter ? <Icon name="filter" />t`Filters` : t`Filter`} */}
              <Icon name="filter" />
            </AButton>

            {showCustomFilter && (
              <div
                ref={filterPanelRef}
                style={{
                  position: "fixed",
                  top: "46px", // 控制垂直位置
                  right: "24px", // 控制靠右边，不会溢出
                  backgroundColor: "#fff",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  padding: "16px",
                  zIndex: 999,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  width: 380,
                }}
              >
                {dashcard.visualization_settings.filterComponentType ===
                "filter1" ? (
                  <>
                    <Form layout="vertical">
                      <Form.Item
                        label={t`Reference`}
                        style={{ marginBottom: "10px" }}
                      >
                        <Input
                          placeholder={t`Please Enter`}
                          allowClear
                          value={customFilterInput.argreference || ""}
                          style={{ width: "168px" }}
                          onChange={e =>
                            setCustomFilterInput(prev => ({
                              ...prev,
                              argreference: e.target.value,
                            }))
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label={t`Operator`}
                        style={{ marginBottom: "10px" }}
                      >
                        <Input
                          placeholder={t`Please Enter`}
                          allowClear
                          value={customFilterInput.argoperator || ""}
                          style={{ width: "168px" }}
                          onChange={e =>
                            setCustomFilterInput(prev => ({
                              ...prev,
                              argoperator: e.target.value,
                            }))
                          }
                        />
                      </Form.Item>
                    </Form>
                    <div style={{ textAlign: "right" }}>
                      <AButton
                        style={{ marginRight: "8px" }}
                        onClick={() => setShowCustomFilter(false)}
                      >
                        {t`Cancel`}
                      </AButton>
                      <AButton
                        type="primary"
                        onClick={() => {
                          // 这里调用查询逻辑（iframe刷新 + 参数更新）
                          handleFilterChange(customFilterInput);
                          setShowCustomFilter(false); // 可选：关闭面板
                        }}
                      >
                        {t`Query`}
                      </AButton>
                    </div>
                  </>
                ) : dashcard.visualization_settings.filterComponentType ===
                  "filter2" ? (
                  <>
                    <Form layout="vertical">
                      <div style={{ display: "flex", gap: "16px" }}>
                        <Form.Item
                          label={t`Reference`}
                          style={{ flex: 1, marginBottom: "10px" }}
                        >
                          <Input
                            placeholder={t`Please Enter`}
                            allowClear
                            value={customFilterInput.argreference || ""}
                            style={{ width: "168px" }}
                            onChange={e =>
                              setCustomFilterInput(prev => ({
                                ...prev,
                                argreference: e.target.value,
                              }))
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          label={t`Transaction ID`}
                          style={{ flex: 1, marginBottom: "10px" }}
                        >
                          <Input
                            style={{ width: "168px" }}
                            allowClear
                            placeholder={t`Please Enter`}
                            value={customFilterInput.argtransactionid || ""}
                            onChange={e =>
                              setCustomFilterInput(prev => ({
                                ...prev,
                                argtransactionid: e.target.value,
                              }))
                            }
                          />
                        </Form.Item>
                      </div>
                      <div style={{ display: "flex", gap: "16px" }}>
                        <Form.Item
                          label={t`Payment Method`}
                          style={{ flex: 1, marginBottom: "10px" }}
                        >
                          <Select
                            placeholder={t`Please Select`}
                            value={
                              customFilterInput.argpaymentmethod || undefined
                            }
                            style={{ width: "168px" }}
                            allowClear
                            onChange={value =>
                              setCustomFilterInput(prev => ({
                                ...prev,
                                argpaymentmethod: value,
                              }))
                            }
                          >
                            {/* <Option value="1">用电户账户</Option>
                                <Option value="2">现金</Option>
                                <Option value="3">支票</Option>
                                <Option value="4">银行转账</Option> */}
                            {paymentMethods.map(method => (
                              <Option key={method.value} value={method.value}>
                                {method.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          label={t`Operator`}
                          style={{ flex: 1, marginBottom: "10px" }}
                        >
                          <Input
                            placeholder={t`Please Enter`}
                            allowClear
                            value={customFilterInput.argoperator || ""}
                            style={{ width: "168px" }}
                            onChange={e =>
                              setCustomFilterInput(prev => ({
                                ...prev,
                                argoperator: e.target.value,
                              }))
                            }
                          />
                        </Form.Item>
                      </div>
                    </Form>
                    <div style={{ textAlign: "right", marginTop: "8px" }}>
                      <AButton
                        style={{ marginRight: "8px" }}
                        onClick={() => setShowCustomFilter(false)}
                      >
                        {t`Cancel`}
                      </AButton>
                      <AButton
                        type="primary"
                        onClick={() => {
                          // 这里调用查询逻辑（iframe刷新 + 参数更新）
                          handleFilterChange(customFilterInput);
                          setShowCustomFilter(false); // 可选：关闭面板
                        }}
                      >
                        {t`Query`}
                      </AButton>
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </>
        ) : null}
        {hasCustomDateRange ? (
          <RangePicker
            popupClassName="custom-picker-dropdown"
            placement="bottomRight"
            defaultValue={[dayjs(), dayjs()] as any}
            presets={datePresets.map(l => ({
              ...l,
              label: (
                <AButton onClick={() => onPresetClick(l)}>{l.label}</AButton>
              ),
            }))}
            onChange={onDateChange}
          />
        ) : null}
        {hasCustomTimeGroup ? (
          <Radio.Group
            defaultValue="month"
            optionType="button"
            buttonStyle="solid"
            value={radioValue}
            onChange={onRadioChange}
          >
            <Radio.Button value="day">{t`Daily`}</Radio.Button>
            <Radio.Button value="month">{t`Monthly`}</Radio.Button>
            <Radio.Button value="year">{t`Annual`}</Radio.Button>
          </Radio.Group>
        ) : null}
        {hasCustomTimeGroup1 ? (
          <Radio.Group
            defaultValue="month"
            optionType="button"
            buttonStyle="solid"
            value={radioValue}
            onChange={onRadioChange}
          >
            <Radio.Button value="month">{t`Monthly`}</Radio.Button>
            <Radio.Button value="quarter">{t`Quarter`}</Radio.Button>
            <Radio.Button value="year">{t`Annual`}</Radio.Button>
          </Radio.Group>
        ) : null}
        {/* 判断是否显示刷新按钮与全屏按钮 */}
        {dashcard.visualization_settings.showRefreshButton ||
        dashcard.visualization_settings.showFullButton ? (
          <Divider type="vertical" />
        ) : null}
        <Space>
          {dashcard.visualization_settings.showRefreshButton ? (
            <Tooltip
              placement="topRight"
              overlayStyle={{ maxWidth: 500 }}
              title={
                t`Data Last Updated on: ` +
                dayjs(lastRefreshTime).format("YYYY-MM-DD HH:mm:ss")
              }
            >
              <AButton onClick={onRefreshClick}>
                <Icon name="refresh" />
              </AButton>
            </Tooltip>
          ) : null}
          {dashcard.visualization_settings.showFullButton ? (
            <AButton onClick={onFullscreenClick}>
              {screenfull.isFullscreen ? (
                <Icon name="contract" />
              ) : (
                <Icon name="expand" />
              )}
            </AButton>
          ) : null}
        </Space>
      </CustomBtnsWrapper>
    </IFrameWrapper>
  );
}

function ForbiddenDomainError({ url }: { url: string }) {
  const isAdmin = useSelector(getUserIsAdmin);
  const { url: docsUrl, showMetabaseLinks } = useDocsUrl(
    "configuring-metabase/settings",
    "allowed-domains-for-iframes-in-dashboards",
  );

  const domain = useMemo(() => {
    try {
      const { hostname } = new URL(url);
      return hostname;
    } catch {
      return url;
    }
  }, [url]);

  const renderMessage = () => {
    if (isAdmin) {
      return jt`If you’re sure you trust this domain, you can add it to your ${(<Link key="link" className={CS.link} to="/admin/settings/general#allowed-iframe-hosts" target="_blank">{t`allowed domains list`}</Link>)} in admin settings.`;
    }
    return showMetabaseLinks
      ? jt`If you’re sure you trust this domain, you can ask an admin to add it to the ${(<ExternalLink key="link" className={CS.link} href={docsUrl}>{t`allowed domains list`}</ExternalLink>)}.`
      : t`If you’re sure you trust this domain, you can ask an admin to add it to the allowed domains list.`;
  };

  return (
    <Box p={12} w="100%" style={{ textAlign: "center" }}>
      <Icon name="lock" color="var(--mb-color-text-dark)" mb="s" />
      <Text color="text-dark">
        {jt`${(
          <Text key="domain" fw="bold" display="inline">
            {domain}
          </Text>
        )} can not be embedded in iframe cards.`}
      </Text>
      <InteractiveText color="text-dark" px="lg" mt="md">
        {renderMessage()}
      </InteractiveText>
    </Box>
  );
}

function GenericError() {
  return (
    <Box p={12} w="100%" style={{ textAlign: "center" }}>
      <Icon name="lock" color="var(--mb-color-text-dark)" mb="s" />
      <Text color="text-dark">
        {t`There was a problem rendering this content.`}
      </Text>
    </Box>
  );
}

Object.assign(IFrameViz, settings);
